'use client';

import { useState, useEffect } from 'react';
import Sidebar from '@/components/common/Sidebar';
import UtilityHeader from '@/components/common/UtilityHeader';
import { ImpersonationProvider, useImpersonation } from '@/contexts/ImpersonationContext';
import { NotificationProvider } from '@/contexts/NotificationContext';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import ToastContainer from '@/components/common/ToastContainer';
import Icon from '@/components/ui/AppIcon';

interface AppLayoutProps {
  children: React.ReactNode;
}

const ImpersonationBanner = () => {
  const { isImpersonating, impersonatedUser, stopImpersonation } = useImpersonation();

  if (!isImpersonating || !impersonatedUser) return null;

  return (
    <div
      className="fixed top-[60px] left-0 right-0 z-[60] flex items-center justify-between px-5 py-2.5 lg:pl-[260px]"
      style={{ background: '#F59E0B' }}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-center gap-2">
        <Icon name="EyeIcon" size={16} className="text-white flex-shrink-0" variant="solid" />
        <span className="font-body text-sm font-semibold text-white">
          Viewing as {impersonatedUser.first_name} {impersonatedUser.last_name}
          <span className="mx-1.5 opacity-70">·</span>
          {impersonatedUser.role.replace(/_/g, ' ')}
          <span className="mx-1.5 opacity-70">—</span>
          <button
            onClick={stopImpersonation}
            className="underline underline-offset-2 hover:opacity-80 transition-standard font-semibold"
          >
            Click to exit preview
          </button>
        </span>
      </div>
      <button
        onClick={stopImpersonation}
        className="flex items-center justify-center w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 transition-standard flex-shrink-0"
        aria-label="Exit preview mode"
      >
        <Icon name="XMarkIcon" size={16} className="text-white" />
      </button>
    </div>
  );
};

const AppLayoutInner = ({ children }: AppLayoutProps) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isImpersonating } = useImpersonation();
  const { currentUser } = useAuth();

  // Announce this logged-in user's presence on a shared realtime channel so the
  // Employee Directory can show who's currently online. Tracks the REAL user
  // (not an impersonated one). Untracks automatically when the app is closed.
  const presenceEmail = currentUser?.email?.toLowerCase() || '';
  const presenceName = `${currentUser?.first_name ?? ''} ${currentUser?.last_name ?? ''}`.trim();
  useEffect(() => {
    if (!presenceEmail) return;
    const supabase = createClient();
    const channel = supabase.channel('online-users', {
      config: { presence: { key: presenceEmail } },
    });
    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        channel.track({ email: presenceEmail, name: presenceName, at: Date.now() });
      }
    });
    return () => { supabase.removeChannel(channel); };
  }, [presenceEmail, presenceName]);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <UtilityHeader
        onMenuToggle={() => setSidebarOpen(p => !p)}
        isSidebarOpen={sidebarOpen}
      />
      <ImpersonationBanner />
      <main
        className={`min-h-screen pt-[60px] lg:pl-[250px] ${isImpersonating ? 'pt-[100px]' : ''}`}
        id="main-content"
      >
        {children}
      </main>
      <ToastContainer />
    </div>
  );
};

const AppLayout = ({ children }: AppLayoutProps) => {
  return (
    <ImpersonationProvider>
      <NotificationProvider>
        <AppLayoutInner>{children}</AppLayoutInner>
      </NotificationProvider>
    </ImpersonationProvider>
  );
};

export default AppLayout;